import React, { useState, useEffect } from 'react'
import {ArrowLeft, Heart, MessageCircleDashed as MessageCircle, ExternalLink} from 'lucide-react'
import { useProjects, Project, MediaItem } from '../hooks/useProjects'
import { useLikes } from '../hooks/useLikes'
import { useComments } from '../hooks/useComments'
import { MediaComments } from './MediaComments'

interface FeedItem {
  project: Project
  media: MediaItem
  mediaIndex: number
}

interface NewsFeedPageProps {
  onBack: () => void
  currentUserId?: string
  onProjectClick: (project: Project) => void
}

export function NewsFeedPage({ onBack, currentUserId, onProjectClick }: NewsFeedPageProps) {
  const { projects, loading, fetchActiveProjects } = useProjects()
  const [feedItems, setFeedItems] = useState<FeedItem[]>([])
  const [selectedMedia, setSelectedMedia] = useState<{ projectId: string; mediaIndex: number } | null>(null)

  useEffect(() => {
    fetchActiveProjects()
  }, [])

  useEffect(() => {
    // Aggregate all media items from active projects
    const items: FeedItem[] = []
    
    projects.forEach(project => {
      if (project.mediaGallery && project.mediaGallery.length > 0) {
        project.mediaGallery.forEach((media, index) => {
          items.push({
            project,
            media,
            mediaIndex: index
          })
        })
      }
    })

    // Sort by upload date (newest first)
    items.sort((a, b) => {
      const dateA = new Date(a.media.uploadedAt).getTime()
      const dateB = new Date(b.media.uploadedAt).getTime()
      return dateB - dateA
    })

    setFeedItems(items)
  }, [projects])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] flex items-center justify-center">
        <div className="text-white text-xl">Loading news feed...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] py-8 px-4 sm:px-6 lg:px-8">
      {/* Media Comments Modal */}
      {selectedMedia && (
        <MediaComments
          projectId={selectedMedia.projectId}
          mediaIndex={selectedMedia.mediaIndex}
          currentUserId={currentUserId}
          onClose={() => setSelectedMedia(null)}
        />
      )}

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">Project News Feed</h1>
          <p className="text-gray-400">Latest posts from all listed projects</p>
        </div>

        {/* Feed */}
        {feedItems.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-gray-400 text-xl">No posts yet. Check back soon!</div>
          </div>
        ) : (
          <div className="space-y-8">
            {feedItems.map((item, idx) => (
              <FeedItemCard
                key={`${item.project._id}-${item.mediaIndex}-${idx}`}
                item={item}
                currentUserId={currentUserId}
                onMediaClick={() => setSelectedMedia({ projectId: item.project._id, mediaIndex: item.mediaIndex })}
                onProjectClick={() => onProjectClick(item.project)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface FeedItemCardProps {
  item: FeedItem
  currentUserId?: string
  onMediaClick: () => void
  onProjectClick: () => void
}

function FeedItemCard({ item, currentUserId, onMediaClick, onProjectClick }: FeedItemCardProps) {
  const { likes, isLiked, likeCount, loading: likesLoading } = useLikes(
    item.project._id,
    item.mediaIndex,
    currentUserId
  )
  const { comments, loading: commentsLoading } = useComments(item.project._id, item.mediaIndex)

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all">
      {/* Project Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {item.project.profileImageUrl && (
            <img
              src={item.project.profileImageUrl}
              alt={item.project.projectName}
              className="w-10 h-10 rounded-full object-cover"
            />
          )}
          <div>
            <button
              onClick={onProjectClick}
              className="text-white font-semibold hover:text-[#60A5FA] transition-colors"
            >
              {item.project.projectName}
            </button>
            <p className="text-gray-500 text-sm">
              {new Date(item.media.uploadedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
        <button
          onClick={onProjectClick}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          title="Visit project"
        >
          <ExternalLink className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Media Content */}
      <div className="relative bg-black">
        {item.media.type === 'image' ? (
          <img
            src={item.media.url}
            alt={item.media.caption || 'Project media'}
            className="w-full max-h-[600px] object-contain"
          />
        ) : (
          <video
            src={item.media.url}
            controls
            className="w-full max-h-[600px]"
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      {/* Caption */}
      {item.media.caption && (
        <div className="p-4 border-b border-white/10">
          <p className="text-gray-300">{item.media.caption}</p>
        </div>
      )}

      {/* Actions */}
      <div className="p-4">
        <div className="flex items-center gap-4 mb-3">
          <button
            onClick={onMediaClick}
            className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <Heart
              className={`w-6 h-6 ${
                isLiked(currentUserId || '') ? 'fill-red-500 text-red-500' : ''
              }`}
            />
            <span className="text-white font-medium">{likeCount}</span>
          </button>
          <button
            onClick={onMediaClick}
            className="flex items-center gap-2 text-gray-400 hover:text-[#60A5FA] transition-colors"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="text-white font-medium">{comments.length}</span>
          </button>
        </div>

        {/* Comment Preview */}
        {comments.length > 0 && (
          <div className="space-y-2">
            <button
              onClick={onMediaClick}
              className="text-gray-400 text-sm hover:text-white transition-colors"
            >
              View all {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
